/**
 * trainingShared.jsx · v2.7.64+E49 · Training Center shared UI kit
 * Header with cross-links between the 3 training surfaces, audience/type
 * chip styling, and the markdown renderer used by the Wiki reader and
 * LMS module dialogs.
 */
import { Link, useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { ArrowLeftCircle, BookOpen, GraduationCap, Library } from "lucide-react";

export const AUDIENCES = ["ADMIN", "DRIVER", "RIDER", "SAFETY", "STAFF"];

export const AUDIENCE_CHIP = {
  ADMIN:  "bg-sky-500/15 text-sky-300 border-sky-500/40",
  DRIVER: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  RIDER:  "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40",
  SAFETY: "bg-red-500/15 text-red-300 border-red-500/40",
  STAFF:  "bg-violet-500/15 text-violet-300 border-violet-500/40",
};

export const TYPE_META = {
  WIKI_ARTICLE: { label: "WIKI",  chip: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  LMS_MODULE:   { label: "LMS",   chip: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
};

const TABS = [
  { href: "/admin/training-center/wiki", label: "Knowledge Center", short: "WIKI", Icon: BookOpen },
  { href: "/admin/training-center/lms",  label: "Learning Center",  short: "LMS",  Icon: GraduationCap },
  { href: "/admin/training-center/kmc",  label: "Management Center", short: "KMC", Icon: Library },
];

export function TrainingHeader({ Icon, title, subtitle, right }) {
  const { pathname } = useLocation();
  return (
    <div className="border-b border-zinc-800 bg-zinc-950/90 backdrop-blur sticky top-0 z-20">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-zinc-500 hover:text-amber-300 transition-colors"
                  data-testid="training-back-link" title="Back to Admin Console">
              <ArrowLeftCircle className="h-6 w-6" />
            </Link>
            <Icon className="h-7 w-7 text-amber-400" />
            <div>
              <h1 className="text-xl font-black tracking-tight text-zinc-100" data-testid="training-page-title">
                {title}
              </h1>
              <p className="text-xs text-zinc-500">{subtitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">{right}</div>
        </div>
        <div className="mt-3 flex items-center gap-1.5" data-testid="training-tabs">
          {TABS.map((t) => {
            const active = pathname === t.href;
            return (
              <Link key={t.href} to={t.href}
                data-testid={`training-tab-${t.short.toLowerCase()}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-bold border transition-colors ${
                  active
                    ? "bg-amber-400/10 border-amber-400/50 text-amber-300"
                    : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200"
                }`}>
                <t.Icon className="h-3.5 w-3.5" />
                {t.label}
                <span className="opacity-60">· {t.short}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** Consistent dark-theme markdown rendering for article/module bodies. */
export function TrainingMarkdown({ children }) {
  return (
    <div className="training-md text-sm leading-relaxed text-zinc-300">
      <ReactMarkdown
        components={{
          h1: ({ children: c }) => <h3 className="text-base font-extrabold text-amber-300 mt-5 mb-2">{c}</h3>,
          h2: ({ children: c }) => <h3 className="text-base font-extrabold text-amber-300 mt-5 mb-2">{c}</h3>,
          h3: ({ children: c }) => <h4 className="text-sm font-bold text-amber-200/90 mt-4 mb-1.5">{c}</h4>,
          p:  ({ children: c }) => <p className="mb-2.5">{c}</p>,
          ul: ({ children: c }) => <ul className="list-disc pl-5 mb-3 space-y-1">{c}</ul>,
          ol: ({ children: c }) => <ol className="list-decimal pl-5 mb-3 space-y-1">{c}</ol>,
          li: ({ children: c }) => <li className="marker:text-amber-500/70">{c}</li>,
          strong: ({ children: c }) => <strong className="text-zinc-100 font-bold">{c}</strong>,
          code: ({ children: c }) => (
            <code className="px-1 py-0.5 rounded bg-zinc-800 text-amber-200 text-[12px] font-mono">{c}</code>
          ),
          a: ({ children: c, href }) => (
            <a href={href} className="text-sky-300 underline decoration-sky-500/40">{c}</a>
          ),
          blockquote: ({ children: c }) => (
            <blockquote className="border-l-2 border-amber-500/50 pl-3 text-zinc-400 italic mb-3">{c}</blockquote>
          ),
        }}
      >
        {children || ""}
      </ReactMarkdown>
    </div>
  );
}

export function AudienceChip({ audience }) {
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded border text-[10px] font-bold ${AUDIENCE_CHIP[audience] || "bg-zinc-800 text-zinc-400 border-zinc-700"}`}>
      {audience}
    </span>
  );
}

export function TypeChip({ type }) {
  const m = TYPE_META[type] || { label: type, chip: "bg-zinc-800 text-zinc-400 border-zinc-700" };
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded border text-[10px] font-bold ${m.chip}`}>
      {m.label}
    </span>
  );
}

export function AgentBadge() {
  return (
    <span className="inline-block px-1.5 py-0.5 rounded border text-[10px] font-bold bg-yellow-500/10 text-yellow-300 border-yellow-500/40"
          title="Auto-generated from a live crawl of the platform codebase">
      AGENT-GENERATED
    </span>
  );
}
